import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Upload, 
  X, 
  Plus,
  UserCheck,
  Mail,
  Phone,
  MapPin,
  AlertCircle,
  Award,
  Briefcase,
  Calendar
} from 'lucide-react';

const EnqueteurCreation = () => {
  const navigate = useNavigate();
  
  // État du formulaire
  const [formData, setFormData] = useState({
    // Informations personnelles
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    adresse: '',
    codePostal: '',
    ville: '',
    
    // Informations professionnelles
    formeJuridique: '',
    numeroCertification: '',
    references: '',
    compteActif: true
  });

  const [aggrements, setAggrements] = useState([]);
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);

  // Gestion des changements de formulaire
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Gestion des agréments
  const handleAggrementsAdd = () => {
    const newAggrement = {
      id: Date.now(),
      nom: '',
      fichier: null,
      dateValidite: '',
      fileName: ''
    };
    setAggrements(prev => [...prev, newAggrement]);
  };

  const handleAggrementsChange = (id, field, value) => {
    setAggrements(prev => prev.map(agr => 
      agr.id === id ? { ...agr, [field]: value } : agr
    ));
  };

  const handleAggrementsFileChange = (id, e) => {
    const file = e.target.files[0];
    if (file) {
      setAggrements(prev => prev.map(agr => 
        agr.id === id ? { ...agr, fichier: file, fileName: file.name } : agr
      ));
    }
  };

  const handleAggrementsRemove = (id) => {
    setAggrements(prev => prev.filter(agr => agr.id !== id));
  };

  // Validation du formulaire - Étape 1
  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.prenom.trim()) newErrors.prenom = 'Prénom requis';
    if (!formData.nom.trim()) newErrors.nom = 'Nom requis';
    if (!formData.email.trim()) {
      newErrors.email = 'Email requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    if (!formData.telephone.trim()) newErrors.telephone = 'Téléphone requis';
    if (!formData.adresse.trim()) newErrors.adresse = 'Adresse requise';
    if (!formData.codePostal.trim()) {
      newErrors.codePostal = 'Code postal requis';
    } else if (!/^\d{5}$/.test(formData.codePostal)) {
      newErrors.codePostal = 'Code postal invalide (5 chiffres requis)';
    }
    if (!formData.ville.trim()) newErrors.ville = 'Ville requise';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validation du formulaire - Étape 2
  const validateStep2 = () => {
    const newErrors = {};
    
    if (!formData.formeJuridique) newErrors.formeJuridique = 'Forme juridique requise';
    if (!formData.numeroCertification.trim()) newErrors.numeroCertification = 'Numéro de certification requis';
    
    // Validation des agréments avec date de validité
    const agrementsErrors = {};
    aggrements.forEach((agr, index) => {
      if (agr.nom && agr.fichier && !agr.dateValidite) {
        agrementsErrors[agr.id] = 'Date de validité requise pour cet agrément';
      }
      if (agr.nom && agr.dateValidite && !agr.fichier) {
        agrementsErrors[agr.id] = 'Fichier requis pour cet agrément';
      }
      if (agr.fichier && !agr.nom.trim()) {
        agrementsErrors[agr.id] = 'Nom de l\'agrément requis';
      }
    });
    
    if (Object.keys(agrementsErrors).length > 0) {
      newErrors.aggrements = agrementsErrors;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation entre les étapes
  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(1);
  };

  // Soumission du formulaire
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateStep2()) {
      return;
    }
    
    // Ici on enverrait les données à l'API
    console.log('Données de l\'enquêteur:', formData);
    console.log('Agréments:', aggrements);
    
    // Redirection vers la liste des enquêteurs
    alert('Enquêteur créé avec succès !');
    navigate('/admin/enqueteurs');
  };

  // Indicateur de progression
  const steps = [
    { number: 1, title: 'Informations personnelles', icon: UserCheck },
    { number: 2, title: 'Informations professionnelles', icon: Award }
  ];

  return (
    <div className="animate-slide-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Link 
            to="/admin/enqueteurs" 
            className="p-2 text-port-gray-600 hover:bg-port-gray-100 rounded-lg transition-colors mr-4"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-port-gray-900">Nouvel Enquêteur</h1>
            <p className="text-port-gray-600 mt-1">
              Créez un nouveau profil d'enquêteur dans le système
            </p>
          </div>
        </div>
      </div>

      {/* Formulaire */}
      <form onSubmit={handleSubmit}>

          <div className="space-y-6">
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium flex items-center">
                  <UserCheck className="h-5 w-5 mr-2" />
                  Informations
                </h3>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Prénom */}
                  <div>
                    <label className="form-label">
                      Prénom <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      className={`form-input ${errors.prenom ? 'border-red-500' : ''}`}
                      placeholder="Prénom de l'enquêteur"
                    />
                    {errors.prenom && (
                      <p className="text-red-500 text-sm mt-1 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.prenom}
                      </p>
                    )}
                  </div>

                  {/* Nom */}
                  <div>
                    <label className="form-label">
                      Nom <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      className={`form-input ${errors.nom ? 'border-red-500' : ''}`}
                      placeholder="Nom de l'enquêteur"
                    />
                    {errors.nom && (
                      <p className="text-red-500 text-sm mt-1 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.nom}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="form-label flex items-center">
                      <Mail className="h-4 w-4 mr-1" />
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`form-input ${errors.email ? 'border-red-500' : ''}`}
                      placeholder="email@exemple.fr"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Téléphone */}
                  <div>
                    <label className="form-label flex items-center">
                      <Phone className="h-4 w-4 mr-1" />
                      Téléphone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      className={`form-input ${errors.telephone ? 'border-red-500' : ''}`}
                      placeholder="06 12 34 56 78"
                    />
                    {errors.telephone && (
                      <p className="text-red-500 text-sm mt-1 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.telephone}
                      </p>
                    )}
                  </div>

                  {/* Forme juridique */}
                  <div>
                    <label className="form-label">
                      Forme Juridique <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="formeJuridique"
                      value={formData.formeJuridique}
                      onChange={handleChange}
                      className={`form-select ${errors.formeJuridique ? 'border-red-500' : ''}`}
                    >
                      <option value="">Sélectionner une forme juridique</option>
                      <option value="Auto-entrepreneur">Auto-entrepreneur</option>
                      <option value="EURL">EURL</option>
                      <option value="SASU">SASU</option>
                      <option value="SAS">SAS</option>
                      <option value="SARL">SARL</option>
                      <option value="SA">SA</option>
                    </select>
                    {errors.formeJuridique && (
                      <p className="text-red-500 text-sm mt-1 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.formeJuridique}
                      </p>
                    )}
                  </div>

                  {/* Numéro de certification */}
                  <div>
                    <label className="form-label flex items-center">
                      <Award className="h-4 w-4 mr-1" />
                      Numéro de Certification <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="numeroCertification"
                      value={formData.numeroCertification}
                      onChange={handleChange}
                      className={`form-input ${errors.numeroCertification ? 'border-red-500' : ''}`}
                      placeholder="CERT-2024-XXX"
                    />
                    {errors.numeroCertification && (
                      <p className="text-red-500 text-sm mt-1 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.numeroCertification}
                      </p>
                    )}
                  </div>

                  {/* Compte actif */}
                  <div className="md:col-span-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="compteActif"
                        checked={formData.compteActif}
                        onChange={handleChange}
                        className="form-checkbox h-5 w-5 text-atl-blue-600"
                      />
                      <span className="ml-2 text-sm text-port-gray-900">
                        Compte actif (l'enquêteur pourra se connecter et recevoir des dossiers)
                      </span>
                    </label>
                  </div>

                </div>
              </div>
            </div>

            {/* Adresse */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium flex items-center">
                  <MapPin className="h-5 w-5 mr-2" />
                  Adresse
                </h3>
              </div>
              <div className="card-body">
                <div className="space-y-6">
                  {/* Adresse */}
                  <div>
                    <label className="form-label">
                      Adresse complète <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="adresse"
                      value={formData.adresse}
                      onChange={handleChange}
                      className={`form-textarea ${errors.adresse ? 'border-red-500' : ''}`}
                      rows="2"
                      placeholder="Numéro et nom de rue"
                    />
                    {errors.adresse && (
                      <p className="text-red-500 text-sm mt-1 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.adresse}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Code postal */}
                    <div>
                      <label className="form-label">
                        Code Postal <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="codePostal"
                        value={formData.codePostal}
                        onChange={handleChange}
                        className={`form-input ${errors.codePostal ? 'border-red-500' : ''}`}
                        placeholder="75001"
                        maxLength="5"
                      />
                      {errors.codePostal && (
                        <p className="text-red-500 text-sm mt-1 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.codePostal}
                        </p>
                      )}
                    </div>

                    {/* Ville */}
                    <div>
                      <label className="form-label">
                        Ville <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="ville"
                        value={formData.ville}
                        onChange={handleChange}
                        className={`form-input ${errors.ville ? 'border-red-500' : ''}`}
                        placeholder="Paris"
                      />
                      {errors.ville && (
                        <p className="text-red-500 text-sm mt-1 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.ville}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Références */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium">Références et Expériences</h3>
              </div>
              <div className="card-body">
                <textarea
                  name="references"
                  value={formData.references}
                  onChange={handleChange}
                  className="form-textarea"
                  rows="6"
                  placeholder="Décrivez l'expérience professionnelle, les qualifications, les références de l'enquêteur..."
                />
                <p className="text-sm text-port-gray-500 mt-2">
                  Ajoutez des informations sur l'expérience, les domaines d'expertise, les formations, etc.
                </p>
              </div>
            </div>

            {/* Agréments */}
            <div className="card">
              <div className="card-header flex items-center justify-between">
                <h3 className="text-lg font-medium flex items-center">
                  <Award className="h-5 w-5 mr-2" />
                  Agréments et Certifications
                </h3>
                <button
                  type="button"
                  onClick={handleAggrementsAdd}
                  className="btn btn-secondary btn-sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter un agrément
                </button>
              </div>
              <div className="card-body">
                {aggrements.length === 0 ? (
                  <div className="text-center py-8 text-port-gray-500">
                    <Award className="h-12 w-12 mx-auto mb-3 text-port-gray-400" />
                    <p>Aucun agrément ajouté. Cliquez sur "Ajouter un agrément" pour commencer.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {aggrements.map((agrement, index) => (
                      <div key={agrement.id} className="border border-port-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-4">
                          <h4 className="text-sm font-medium text-port-gray-900">
                            Agrément #{index + 1}
                          </h4>
                          <button
                            type="button"
                            onClick={() => handleAggrementsRemove(agrement.id)}
                            className="p-1 text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Nom de l'agrément */}
                          <div>
                            <label className="form-label">Nom de l'agrément</label>
                            <input
                              type="text"
                              value={agrement.nom}
                              onChange={(e) => handleAggrementsChange(agrement.id, 'nom', e.target.value)}
                              className="form-input"
                              placeholder="Ex: Agrément Expert Auto"
                            />
                          </div>

                          {/* Date de validité */}
                          <div>
                            <label className="form-label flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              Date de validité
                            </label>
                            <input
                              type="date"
                              value={agrement.dateValidite}
                              onChange={(e) => handleAggrementsChange(agrement.id, 'dateValidite', e.target.value)}
                              className="form-input"
                            />
                          </div>

                          {/* Fichier */}
                          <div className="md:col-span-2">
                            <label className="form-label flex items-center">
                              <Upload className="h-4 w-4 mr-1" />
                              Document
                            </label>
                            <div className="flex items-center space-x-3">
                              <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-port-gray-300 rounded-lg hover:border-atl-blue-500 cursor-pointer transition-colors">
                                <Upload className="h-5 w-5 text-port-gray-400 mr-2" />
                                <span className="text-sm text-port-gray-600">
                                  {agrement.fileName || 'Choisir un fichier'}
                                </span>
                                <input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={(e) => handleAggrementsFileChange(agrement.id, e)}
                                  className="hidden"
                                />
                              </label>
                            </div>
                          </div>
                        </div>

                        {errors.aggrements && errors.aggrements[agrement.id] && (
                          <p className="text-red-500 text-sm mt-2 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {errors.aggrements[agrement.id]}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-sm text-port-gray-500 mt-4">
                  Les agréments permettent de certifier les compétences de l'enquêteur. Chaque agrément doit comporter un document et une date de validité.
                </p>
              </div>
            </div>

            {/* Boutons de navigation */}
            <div className="flex justify-between">
              <button type="button" onClick={handlePrevious} className="btn btn-secondary">
                Précédent
              </button>
              <div className="flex space-x-4">
                <Link to="/admin/enqueteurs" className="btn btn-secondary">
                  Annuler
                </Link>
                <button type="submit" className="btn btn-primary">
                  <Save className="h-4 w-4 mr-2" />
                  Créer l'enquêteur
                </button>
              </div>
            </div>
          </div>
      </form>
    </div>
  );
};

export default EnqueteurCreation;

